// ODS-EWO-033 P1. What the shell knows about the applications it frames, in one
// place, joined from the two upstreams that already own the answers: the
// catalogue owns lifecycle, availability and what the customer holds; the
// platform owns entitlements. The shell adds no authority and decides no
// access; every route is still admitted by its own guard and every mutation
// still goes to the application that owns it.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { CatalogApplication, CatalogCustomerState, CatalogPreference } from "@odingard/sdk-client";
import { api } from "../lib/api";
import {
  APPLICATION_MANIFESTS,
  isEntitled,
  manifestForPath,
  type ApplicationManifest,
} from "./applications";

/** An application the switcher can offer, and why it can or cannot be entered. */
export type ShellApplication = {
  readonly manifest: ApplicationManifest;
  /** The catalogue row, when the catalogue answered. */
  readonly catalog: CatalogApplication | undefined;
  /** The customer's state for it, when the catalogue answered. */
  readonly customerState: CatalogCustomerState | undefined;
  /** The organization holds every entitlement key the application requires. */
  readonly entitled: boolean;
  readonly pinned: boolean;
  readonly lastOpenedAt: string | null;
  /**
   * Enterable now. Entitlement is the shell's test, not the catalogue's
   * `canLaunch`: a route admits a caller on the keys its guard checks, and the
   * two disagree while a subscription is settling.
   */
  readonly canEnter: boolean;
  /** Where a customer who cannot enter should be sent, if anywhere. */
  readonly offer: "trial" | "subscribe" | "coming_soon" | null;
  readonly name: string;
  readonly icon: string;
};

/**
 * How much of the shell's own picture is trustworthy. The catalogue and the
 * platform are separate services with separate authority, so the interesting
 * failure is not "nothing answered" but "one of them did". A shell that fails
 * harder than the applications it frames would be worse than no shell, so a
 * partial answer degrades to navigation that still works and says so, rather
 * than each surface inventing a fallback (ODS-UX-001 §11).
 */
export type ShellReach = "complete" | "entitlements_only" | "catalog_only" | "unavailable";

export type Shell = {
  readonly applications: readonly ShellApplication[];
  /** Entitled applications, pinned first, then most recently opened. */
  readonly entered: readonly ShellApplication[];
  /** The application the current path belongs to, if any. */
  readonly current: ApplicationManifest | undefined;
  readonly reach: ShellReach;
  /** The platform answered, so `canEnter` is a real answer rather than a guess. */
  readonly entitlementsKnown: boolean;
  readonly loading: boolean;
  readonly reload: () => void;
  readonly setPinned: (slug: string, pinned: boolean) => Promise<void>;
  readonly recordOpen: (slug: string) => void;
};

type Loaded = {
  apps: CatalogApplication[] | null;
  states: CatalogCustomerState[] | null;
  preferences: CatalogPreference[] | null;
  entitlements: { key: string; status: string }[] | null;
};

const EMPTY: Loaded = { apps: null, states: null, preferences: null, entitlements: null };

/** Neither upstream is allowed to take the other down. */
async function settle<T>(work: Promise<T>): Promise<T | null> {
  try {
    return await work;
  } catch {
    return null;
  }
}

function offerFor(
  entitled: boolean,
  state: CatalogCustomerState | undefined,
): ShellApplication["offer"] {
  if (entitled) return null;
  if (!state) return null;
  if (state.state === "coming_soon" || state.lifecycleStatus === "coming_soon") return "coming_soon";
  if (state.canTrial) return "trial";
  if (state.canSubscribe) return "subscribe";
  return null;
}

export function useShell(orgId: string, pathname: string): Shell {
  const [loaded, setLoaded] = useState<Loaded>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!orgId) {
      setLoaded(EMPTY);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    void (async () => {
      const [catalog, states, preferences, entitlements] = await Promise.all([
        settle(api.catalogApplications({ delivery: "platform", pageSize: 100 })),
        settle(api.catalogState(orgId)),
        settle(api.catalogPreferences(orgId)),
        settle(api.entitlements(orgId)),
      ]);
      if (!live) return;
      setLoaded({
        apps: catalog ? catalog.applications.filter((a) => a.delivery === "platform") : null,
        states: states ? states.states : null,
        preferences: preferences ? preferences.preferences : null,
        entitlements: entitlements ? entitlements.entitlements : null,
      });
      setLoading(false);
    })();
    return () => {
      live = false;
    };
  }, [orgId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const setPinned = useCallback(
    async (slug: string, pinned: boolean) => {
      if (!orgId) return;
      if (pinned) await api.pinCatalogApp(orgId, slug);
      else await api.unpinCatalogApp(orgId, slug);
      setNonce((n) => n + 1);
    },
    [orgId],
  );

  const recordOpen = useCallback(
    (slug: string) => {
      // Advisory telemetry: it orders "recently used" and gates nothing, so a
      // failure here must never interrupt a navigation.
      if (!orgId) return;
      void api.recordCatalogOpen(orgId, slug).catch(() => undefined);
    },
    [orgId],
  );

  return useMemo(() => {
    const catalogBySlug = new Map((loaded.apps ?? []).map((a) => [a.slug, a]));
    const stateBySlug = new Map((loaded.states ?? []).map((s) => [s.slug, s]));
    const prefBySlug = new Map((loaded.preferences ?? []).map((p) => [p.slug, p]));

    const applications: ShellApplication[] = APPLICATION_MANIFESTS.map((manifest) => {
      const catalog = catalogBySlug.get(manifest.catalogSlug);
      const customerState = stateBySlug.get(manifest.catalogSlug);
      const pref = prefBySlug.get(manifest.catalogSlug);
      const entitled = isEntitled(manifest, loaded.entitlements);
      return {
        manifest,
        catalog,
        customerState,
        entitled,
        pinned: pref?.pinned ?? false,
        lastOpenedAt: pref?.lastOpenedAt ?? null,
        canEnter: entitled,
        offer: offerFor(entitled, customerState),
        // The manifest names the surface, which the catalogue cannot: the
        // catalogue sells Policy AI, and Governance & Compliance is one of its
        // surfaces rather than a row of its own.
        name: manifest.surfaceOf ? manifest.name : catalog?.name ?? manifest.name,
        icon: manifest.icon,
      };
    });

    const entered = applications
      .filter((a) => a.canEnter)
      .sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        if (a.lastOpenedAt !== b.lastOpenedAt) {
          if (!a.lastOpenedAt) return 1;
          if (!b.lastOpenedAt) return -1;
          return a.lastOpenedAt < b.lastOpenedAt ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      });

    const hasCatalog = loaded.apps !== null && loaded.states !== null;
    const hasEntitlements = loaded.entitlements !== null;
    const reach: ShellReach = hasCatalog
      ? hasEntitlements
        ? "complete"
        : "catalog_only"
      : hasEntitlements
        ? "entitlements_only"
        : "unavailable";

    return {
      applications,
      entered,
      current: manifestForPath(pathname),
      reach,
      entitlementsKnown: hasEntitlements,
      loading,
      reload,
      setPinned,
      recordOpen,
    };
  }, [loaded, pathname, loading, reload, setPinned, recordOpen]);
}
